'use client';

import { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  TextField,
  Typography,
} from '@mui/material';

type FixedIncomeOption = {
  id: string;
  idCem: string;
  name: string;
  titleType: string;
  indexer: string;
  conversionDate: string | null;
  maturityDate: string | null;
};

type FixedIncomeCompareDialogProps = {
  open: boolean;
  onClose: () => void;
  selectedIds: string[];
  onSelectedIdsChange: (ids: string[]) => void;
  maxTotal?: number;
  currentTitles: string;
};

// The Treasury universe is small but keeps growing at the long end; a short
// cap ordered by maturity hid the newest series from the default list.
const MAX_OPTIONS = 200;
const TITLE_LABELS: Record<string, string> = {
  PREFIXADO: 'Prefixado',
  'PREFIXADO COM JUROS SEMESTRAIS': 'Prefixado com juros semestrais',
  SELIC: 'Selic',
  'IPCA+': 'IPCA+',
  'IPCA+ COM JUROS SEMESTRAIS': 'IPCA+ com juros semestrais',
  'IGPM+ COM JUROS SEMESTRAIS': 'IGP-M+ com juros semestrais',
  'RENDA+ APOSENTADORIA EXTRA': 'RendA+ aposentadoria extra',
  'EDUCA+': 'Educa+',
};

const titleLabel = (value: string) => TITLE_LABELS[value] ?? value.replaceAll('_', ' ').toLowerCase();
const titleTag = (option: FixedIncomeOption) => {
  const value = `${option.titleType} ${option.indexer}`.toUpperCase();
  if (value.includes('IPCA')) return 'IPCA+';
  if (value.includes('SELIC')) return 'Selic';
  if (value.includes('PREFIXADO')) return 'Prefixado';
  if (value.includes('IGPM')) return 'IGP-M+';
  return titleLabel(option.titleType);
};

const instrumentCode = (option: FixedIncomeOption) => {
  const seriesDate = option.conversionDate ?? option.maturityDate;
  const year = seriesDate?.slice(2, 4) ?? option.name.match(/20(\d{2})/)?.[1] ?? '';
  const fullYear = seriesDate?.slice(0, 4) ?? '';
  const title = option.titleType.toUpperCase();
  if (title.includes('IPCA')) return `NTNB${year}`;
  if (title.includes('SELIC')) return `LFT${fullYear}`;
  if (title.includes('PREFIXADO')) return `LTN${fullYear}`;
  if (title.includes('IGPM')) return `NTNC${year}`;
  if (title.includes('RENDA')) return `RENDA+${fullYear}`;
  if (title.includes('EDUCA')) return `EDUCA+${fullYear}`;
  return option.idCem;
};

const displayTitle = (option: FixedIncomeOption) => {
  const year =
    option.conversionDate?.slice(0, 4) ?? option.name.match(/20\d{2}/)?.[0] ?? option.maturityDate?.slice(0, 4) ?? '';
  return `Tesouro ${titleLabel(option.titleType)}${year ? ` ${year}` : ''}`;
};

export default function FixedIncomeCompareDialog({
  open,
  onClose,
  selectedIds,
  onSelectedIdsChange,
  maxTotal = 4,
  currentTitles,
}: FixedIncomeCompareDialogProps) {
  const [options, setOptions] = useState<FixedIncomeOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!open || options.length > 0) return;
    const controller = new AbortController();
    setLoading(true);
    setError(false);
    const maturityFrom = new Date().toISOString().slice(0, 10);
    fetch(`/api/renda-fixa/catalogo?maturityFrom=${maturityFrom}`, { cache: 'no-store', signal: controller.signal })
      .then((result) =>
        result.ok ? (result.json() as Promise<{ data: FixedIncomeOption[] }>) : Promise.reject(new Error('catalog')),
      )
      .then((payload) => setOptions(payload.data))
      .catch((reason: unknown) => {
        if (reason instanceof DOMException && reason.name === 'AbortError') return;
        setError(true);
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [open, options.length]);

  const selectedOptions = useMemo(
    () => options.filter((option) => selectedIds.includes(option.id)),
    [options, selectedIds],
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='sm'>
      <DialogTitle>Comparar títulos</DialogTitle>
      <DialogContent>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 1.25 }}>
          Títulos atuais: <strong>{currentTitles}</strong>. Selecione até {maxTotal} títulos no total.
        </Typography>

        {error ? (
          <Typography color='error' variant='body2' sx={{ mb: 1 }}>
            Não foi possível carregar os títulos disponíveis.
          </Typography>
        ) : null}
        <Autocomplete
          multiple
          options={options}
          value={selectedOptions}
          loading={loading}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          getOptionDisabled={(option) =>
            selectedOptions.length >= maxTotal && !selectedOptions.some((selected) => selected.id === option.id)
          }
          getOptionLabel={(option) => `${instrumentCode(option)} - ${displayTitle(option)}`}
          filterOptions={(items, state) => {
            const query = state.inputValue.trim().toLowerCase();
            if (!query) return items.slice(0, MAX_OPTIONS);
            return items
              .filter((option) =>
                `${instrumentCode(option)} ${displayTitle(option)} ${option.titleType}`.toLowerCase().includes(query),
              )
              .slice(0, MAX_OPTIONS);
          }}
          onChange={(_, nextOptions) => onSelectedIdsChange(nextOptions.slice(0, maxTotal).map((option) => option.id))}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.id}
                label={`${instrumentCode(option)} - ${displayTitle(option)}`}
                sx={{ maxWidth: 'calc(100% - 12px)' }}
              />
            ))
          }
          renderOption={(props, option) => {
            const { key, ...optionProps } = props;
            return (
              <Box component='li' key={key} {...optionProps} sx={{ py: 1 }}>
                <Box sx={{ minWidth: 72 }}>
                  <Typography fontWeight={700}>{instrumentCode(option)}</Typography>
                  <Typography variant='caption' color='text.secondary'>
                    {titleTag(option)}
                  </Typography>
                </Box>
                <Box sx={{ ml: 2, flex: 1, minWidth: 0 }}>
                  <Typography
                    variant='body2'
                    fontWeight={600}
                    sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {displayTitle(option)}
                  </Typography>
                  <Typography variant='caption' color='text.secondary'>
                    {option.idCem}
                  </Typography>
                </Box>
              </Box>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              size='small'
              placeholder='Buscar título ou código para comparar'
              inputProps={{ ...params.inputProps, suppressHydrationWarning: true }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          PaperComponent={(props) => (
            <Paper
              {...props}
              elevation={6}
              sx={{ borderRadius: 2, mt: 1, border: '1px solid', borderColor: 'divider' }}
            />
          )}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant='outlined'>
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
